import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { CardDescription } from '@/components/ui/card'
import { useProjects } from '@/hooks/useProjects'

function matchesSearch(project, q) {
  if (!q?.trim()) return true
  const s = q.trim().toLowerCase()
  return (
    (project.title && project.title.toLowerCase().includes(s)) ||
    (project.excerpt && project.excerpt.toLowerCase().includes(s)) ||
    (project.category && String(project.category).toLowerCase().includes(s))
  )
}

const ProjectsGrid = ({ searchQuery = '' }) => {
  const { projects: publicProjects, loading } = useProjects(true)
  const [viewMode, setViewMode] = useState('grid')

  const filtered = useMemo(
    () => publicProjects.filter((p) => matchesSearch(p, searchQuery)),
    [publicProjects, searchQuery]
  )

  if (loading) {
    return (
      <section className="py-[120px] px-0 bg-secondary min-h-screen">
        <div className="max-w-[1600px] mx-auto text-center px-[5%]">
          <p className="text-muted-foreground">Loading projects...</p>
        </div>
      </section>
    )
  }

  if (publicProjects.length === 0 && !loading) {
    return null
  }

  if (filtered.length === 0 && searchQuery.trim()) {
    return (
      <section className="py-[120px] px-0 relative z-10 bg-white">
        <div className="max-w-[1400px] mx-auto px-[5%] text-center text-muted-foreground text-sm">
          No projects match “{searchQuery.trim()}”.
        </div>
      </section>
    )
  }

  return (
    <section className="py-[120px] px-0 relative z-10" style={{ backgroundColor: 'rgb(255, 255, 255)' }}>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-100px' }}
        transition={{ duration: 0.8 }}
        className="w-full"
      >
        <div className="flex justify-between items-center mb-12 px-[5%] max-w-[1400px] mx-auto">
          <motion.h2
            id="projects"
            data-section="projects"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-xl font-semibold tracking-tight text-foreground"
          >
            Selected Projects
          </motion.h2>
          <ToggleGroup value={viewMode} onValueChange={setViewMode}>
            <ToggleGroupItem value="grid">Grid</ToggleGroupItem>
            <ToggleGroupItem value="list">List</ToggleGroupItem>
          </ToggleGroup>
        </div>

        {viewMode === 'grid' ? (
          <div className="px-[5%] max-w-[1400px] mx-auto">
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 [column-fill:_balance] [&>*]:mb-6">
              {filtered.map((project, index) => {
                const heightClass = index % 3 === 0 ? 'h-64' : index % 3 === 1 ? 'h-80' : 'h-96'
                return (
                  <motion.div
                    key={project.id}
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-50px' }}
                    transition={{ duration: 0.6, delay: (index % 12) * 0.03 }}
                    whileHover={{ y: -4, transition: { duration: 0.3 } }}
                    className="break-inside-avoid"
                  >
                    <Link
                      to={project.source === 'hub' ? `/project/hub/${project.hubSlug}` : `/project/${project.id}`}
                      className="no-underline block"
                    >
                      <div
                        className={`w-full ${heightClass} bg-tertiary bg-cover bg-center relative overflow-hidden rounded-2xl shadow-md`}
                        style={{
                          backgroundImage: project.thumbnail_url ? `url(${project.thumbnail_url})` : 'none',
                        }}
                      >
                        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/10 to-black/60" />
                        <div className="absolute inset-0 flex flex-col justify-end p-5">
                          <div className="text-white">
                            <span className="inline-block px-3 py-1 rounded-full text-xs uppercase tracking-wider mb-2 text-white/90 bg-white/20 backdrop-blur-sm border border-white/30">
                              {project.category}
                            </span>
                            <h2 className="text-lg font-semibold mb-1 text-white drop-shadow">{project.title}</h2>
                            {project.excerpt && (
                              <CardDescription className="text-sm text-white/90 drop-shadow max-w-[90%]">
                                {project.excerpt}
                              </CardDescription>
                            )}
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                )
              })}
            </div>
          </div>
        ) : (
          <div className="px-[5%] max-w-[1400px] mx-auto">
            <div className="divide-y divide-border">
              {filtered.map((project, index) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: (index % 10) * 0.03 }}
                  className="py-4"
                >
                  <Link
                    to={project.source === 'hub' ? `/project/hub/${project.hubSlug}` : `/project/${project.id}`}
                    className="no-underline block"
                  >
                    <div className="flex items-center gap-4">
                      {project.thumbnail_url && (
                        <img
                          src={project.thumbnail_url}
                          alt={project.title}
                          className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="inline-block px-3 py-1 rounded-full text-xs uppercase bg-muted text-muted-foreground border border-border">
                            {project.category}
                          </span>
                          <h2 className="text-lg font-semibold truncate">{project.title}</h2>
                        </div>
                        {project.excerpt && (
                          <p className="text-sm text-muted-foreground line-clamp-2">{project.excerpt}</p>
                        )}
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </motion.div>
    </section>
  )
}

export default ProjectsGrid
